import React from "react";
import { Dialog } from "primereact/dialog";
import { Chart } from "primereact/chart";
import { TabView, TabPanel } from "primereact/tabview";
import { MeterGroup } from "primereact/metergroup";
import { normalizeID } from "./dashboardStatus";
import { calculateAge } from "../../utils/calculateAge";

const ChapitreDetailsDialog = ({ activeChapitre, visible, onHide, users }) => {
  const chapitreUsers = activeChapitre
    ? users.filter((user) => normalizeID(user.ChapitreID) === normalizeID(activeChapitre.ChapitreID))
    : [];
  const registrationByFamily = new Map();
  chapitreUsers.forEach((user) => {
    const registrationDate = user.RegistrationDateTime ? new Date(user.RegistrationDateTime) : null;
    if (!registrationDate || Number.isNaN(registrationDate.getTime())) {
      return;
    }
    const key = user.FamilyID ?? user.UserID;
    const existing = registrationByFamily.get(key);
    if (!existing || registrationDate < existing) {
      registrationByFamily.set(key, registrationDate);
    }
  });

  const getWeekStart = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    d.setDate(d.getDate() - ((day + 6) % 7));
    return d;
  };
  const formatWeekLabel = (date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const registrationDates = Array.from(registrationByFamily.values());
  const registrationProgressData = (() => {
    if (registrationDates.length === 0) {
      return { labels: [], datasets: [] };
    }

    const minDate = new Date(Math.min(...registrationDates.map((date) => date.getTime())));
    const maxDate = new Date(Math.max(...registrationDates.map((date) => date.getTime())));
    const firstWeek = getWeekStart(minDate);
    firstWeek.setDate(firstWeek.getDate() - 7);
    const lastWeek = getWeekStart(maxDate);
    lastWeek.setDate(lastWeek.getDate() + 7);
    const weeklyRegistrations = {};

    registrationDates.forEach((date) => {
      const weekKey = formatWeekLabel(getWeekStart(date));
      weeklyRegistrations[weekKey] = (weeklyRegistrations[weekKey] || 0) + 1;
    });

    const labels = [];
    const values = [];
    const cursor = new Date(firstWeek);
    while (cursor <= lastWeek) {
      const key = formatWeekLabel(cursor);
      labels.push(key);
      values.push(weeklyRegistrations[key] || 0);
      cursor.setDate(cursor.getDate() + 7);
    }

    return {
      labels,
      datasets: [
        {
          label: "Weekly registrations",
          data: values,
          borderColor: "#4CAF50",
          backgroundColor: "rgba(76, 175, 80, 0.2)",
          fill: true,
          tension: 0.25
        }
      ]
    };
  })();
  const registrationProgressOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const menAgeBuckets = { "<7": 0, "7-12": 0, "13-18": 0, "19-24": 0, "25-34": 0, "35-44": 0, "45-54": 0, "55+": 0 };
  const womenAgeBuckets = { "<7": 0, "7-12": 0, "13-18": 0, "19-24": 0, "25-34": 0, "35-44": 0, "45-54": 0, "55+": 0 };
  const validAges = [];

  chapitreUsers.forEach((user) => {
    if (!user.BirthDate) {
      return;
    }
    const age = calculateAge(user.BirthDate);
    if (Number.isNaN(age)) {
      return;
    }
    validAges.push(age);
    const gender = String(user.Gender || "").toLowerCase();
    const targetBuckets = ["m", "male", "man", "homme"].includes(gender)
      ? menAgeBuckets
      : ["f", "female", "woman", "femme"].includes(gender)
        ? womenAgeBuckets
        : null;

    if (!targetBuckets) {
      return;
    }

    if (age < 7) targetBuckets["<7"]++;
    else if (age <= 12) targetBuckets["7-12"]++;
    else if (age <= 18) targetBuckets["13-18"]++;
    else if (age <= 24) targetBuckets["19-24"]++;
    else if (age <= 34) targetBuckets["25-34"]++;
    else if (age <= 44) targetBuckets["35-44"]++;
    else if (age <= 54) targetBuckets["45-54"]++;
    else targetBuckets["55+"]++;
  });

  const ageDistributionData = {
    labels: Object.keys(menAgeBuckets),
    datasets: [
      {
        label: "Men",
        data: Object.values(menAgeBuckets),
        borderColor: "#42A5F5",
        backgroundColor: "rgba(66, 165, 245, 1)",
        fill: true,
      },
      {
        label: "Women",
        data: Object.values(womenAgeBuckets),
        borderColor: "#EC407A",
        backgroundColor: "rgba(236, 64, 122, 1)",
        fill: true,
      }
    ]
  };

  const ageDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };
  const sortedAges = [...validAges].sort((a, b) => a - b);
  const averageAge = sortedAges.length > 0
    ? sortedAges.reduce((sum, age) => sum + age, 0) / sortedAges.length
    : null;
  const ageVariance = sortedAges.length > 0 && averageAge !== null
    ? sortedAges.reduce((sum, age) => sum + ((age - averageAge) ** 2), 0) / sortedAges.length
    : null;
  const medianAge = sortedAges.length > 0
    ? (sortedAges.length % 2 === 1
      ? sortedAges[Math.floor(sortedAges.length / 2)]
      : (sortedAges[sortedAges.length / 2 - 1] + sortedAges[sortedAges.length / 2]) / 2)
    : null;

  const menCount = chapitreUsers.filter((user) =>
    ["m", "male", "man", "homme"].includes(String(user.Gender || "").toLowerCase())
  ).length;
  const womenCount = chapitreUsers.filter((user) =>
    ["f", "female", "woman", "femme"].includes(String(user.Gender || "").toLowerCase())
  ).length;
  const genderDistributionValues = [
    { label: "Men", value: menCount, color: "#42A5F5" },
    { label: "Women", value: womenCount, color: "#EC407A" },
  ];

  return (
    <Dialog
      header={activeChapitre?.Name || "Chapitre details"}
      visible={visible}
      style={{ width: "50vw" }}
      onHide={onHide}
    >
      {activeChapitre ? (
        <div>
          <TabView>
            <TabPanel header="Time distribution">
              {registrationProgressData.labels.length > 0 ? (
                <div style={{ width: "100%", height: "260px" }}>
                  <Chart
                    type="line"
                    data={registrationProgressData}
                    options={registrationProgressOptions}
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
              ) : (
                <p>No registration timeline data available.</p>
              )}
            </TabPanel>
            <TabPanel header="Age distribution">
              <div className="mb-3">
                <strong>Average age:</strong> {averageAge === null ? "N/A" : averageAge.toFixed(1)}
                <span className="mx-2">|</span>
                <strong>Median age:</strong> {medianAge === null ? "N/A" : medianAge.toFixed(1)}
                <span className="mx-2">|</span>
                <strong>Variance:</strong> {ageVariance === null ? "N/A" : ageVariance.toFixed(2)}
              </div>
              <div className="w-full" style={{ width: "100%", height: "320px" }}>
                <Chart
                  type="bar"
                  data={ageDistributionData}
                  options={ageDistributionOptions}
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
            </TabPanel>
            <TabPanel header="Gender distribution">
              <MeterGroup values={genderDistributionValues} max={Math.max(1, chapitreUsers.length)} />
            </TabPanel>
          </TabView>
        </div>
      ) : (
        <p>No chapitre selected.</p>
      )}
    </Dialog>
  );
};

export default ChapitreDetailsDialog;
